import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  ReactNode,
  useCallback,
} from 'react';
import { User as FirebaseUser, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../config/firebase';
import authService from '../services/auth.service';
import hubService from '../services/hub.service';
import type { User, Cliente, UserSession } from '../types';
import { BUSINESS_ROLES, BusinessRole } from '../types/portal.types';

interface AuthContextType {
  user: User | null;
  cliente: Cliente | null;
  firebaseUser: FirebaseUser | null;
  loading: boolean;
  isAuthenticated: boolean;
  permissions: string[];
  entidadeId: string | null;
  isBusinessUser: boolean;
  isAdmin: boolean;
  purchasedApps: string[];
  entityServiceTypes: string[];
  entityServiceTypesLoaded: boolean;
  profileUpdateRequired: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  updateCliente: (updates: Partial<Cliente>) => void;
  clearProfileUpdateRequired: () => void;
  hasPermission: (permission: string | string[]) => boolean;
  hasPurchasedApp: (appCode: string) => boolean;
  hasServiceType: (code: string) => boolean;
  refreshPurchasedApps: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [cliente, setCliente] = useState<Cliente | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [entidadeId, setEntidadeId] = useState<string | null>(null);
  const [purchasedApps, setPurchasedApps] = useState<string[]>([]);
  const [entityServiceTypes, setEntityServiceTypes] = useState<string[]>([]);
  const [entityServiceTypesLoaded, setEntityServiceTypesLoaded] = useState(false);
  const [profileUpdateRequired, setProfileUpdateRequired] = useState(false);
  const isLoggingInRef = useRef(false); // Flag to prevent race condition during login

  // Fetch purchased apps from API
  const fetchPurchasedApps = useCallback(async () => {
    try {
      const codes = await hubService.getPurchasedAppCodes();
      setPurchasedApps(codes);
    } catch (error) {
      console.error('Error fetching purchased apps:', error);
      setPurchasedApps([]);
    }
  }, []);

  // Fetch active service type codes for the entity
  const fetchEntityServiceTypes = useCallback(async () => {
    try {
      const { default: api } = await import('../services/api');
      const res = await api.get('/portal/services/my-types');
      setEntityServiceTypes(res.data?.data || []);
    } catch {
      setEntityServiceTypes([]);
    } finally {
      setEntityServiceTypesLoaded(true);
    }
  }, []);

  // Restore session from storage
  const restoreSession = useCallback(() => {
    const session = authService.getCurrentSession();
    if (session?.status) {
      setUser(session.user || null);
      setCliente(session.cliente || null);
      // Restore permissions and entidadeId if stored
      if (session.permissions) {
        setPermissions(session.permissions);
      }
      if (session.entidadeId) {
        setEntidadeId(session.entidadeId);
      }
      if (session.requiresProfileUpdate) {
        setProfileUpdateRequired(true);
      }
      // Fetch purchased apps and entity service types after restoring session
      fetchPurchasedApps();
      if (BUSINESS_ROLES.includes(session.user?.role as BusinessRole)) {
        fetchEntityServiceTypes();
      }
    }
  }, [fetchPurchasedApps]);

  // Listen for Firebase auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      // Skip full processing if we're in the middle of logging in (to prevent race condition)
      if (isLoggingInRef.current) {
        setFirebaseUser(fbUser);
        return;
      }

      setFirebaseUser(fbUser);

      if (fbUser) {
        // User is signed in, restore session data
        restoreSession();
      } else {
        // User is signed out
        setUser(null);
        setCliente(null);
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, [restoreSession]);

  const login = async (username: string, password: string): Promise<void> => {
    console.log('[AuthContext] login() called');
    setLoading(true);
    isLoggingInRef.current = true; // Prevent onAuthStateChanged from interfering
    try {
      console.log('[AuthContext] Calling authService.login...');
      const session = await authService.login({ username, password });
      console.log('[AuthContext] Login successful, setting user:', session.user?.username);
      setUser(session.user || null);
      setCliente(session.cliente || null);
      // Set permissions and entidadeId if available
      if (session.permissions) {
        setPermissions(session.permissions);
      }
      if (session.entidadeId) {
        setEntidadeId(session.entidadeId);
      }
      if (session.requiresProfileUpdate) {
        setProfileUpdateRequired(true);
      }

      // Wait for Firebase auth state to update (the signInWithCustomToken triggers onAuthStateChanged)
      // This ensures firebaseUser is set before we return
      await new Promise<void>((resolve) => {
        const checkAuth = () => {
          if (auth.currentUser) {
            setFirebaseUser(auth.currentUser);
            resolve();
          } else {
            setTimeout(checkAuth, 50);
          }
        };
        checkAuth();
      });

      // Fetch purchased apps and entity service types after login
      fetchPurchasedApps();
      if (BUSINESS_ROLES.includes(session.user?.role as BusinessRole)) {
        fetchEntityServiceTypes();
      }
    } finally {
      isLoggingInRef.current = false; // Re-enable onAuthStateChanged
      setLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    setLoading(true);
    try {
      await authService.logout();
      setUser(null);
      setCliente(null);
      setFirebaseUser(null);
      setPermissions([]);
      setEntidadeId(null);
      setPurchasedApps([]);
      setEntityServiceTypes([]);
      setEntityServiceTypesLoaded(false);
      setProfileUpdateRequired(false);
    } finally {
      setLoading(false);
    }
  };

  // Check if user has purchased a specific app
  const hasPurchasedApp = useCallback((appCode: string): boolean => {
    return purchasedApps.includes(appCode);
  }, [purchasedApps]);

  // Check if entity has a specific service type active
  const hasServiceType = useCallback((code: string): boolean => {
    if (!entityServiceTypesLoaded) return true; // still loading — don't hide items yet
    if (entityServiceTypes.includes(code)) return true;
    // Role-based fallback: PFLOJ always has ECM, PFEVT always has EVT, PFONG always has DNT
    const role = user?.role;
    if (code === 'ECM' && role === 'PFLOJ') return true;
    if (code === 'EVT' && role === 'PFEVT') return true;
    if (code === 'DNT' && role === 'PFONG') return true;
    return false;
  }, [entityServiceTypes, entityServiceTypesLoaded, user?.role]);

  // Refresh purchased apps (useful after purchasing a new app)
  const refreshPurchasedApps = useCallback(async (): Promise<void> => {
    await fetchPurchasedApps();
  }, [fetchPurchasedApps]);

  // Check if user has permission
  const hasPermission = useCallback((permission: string | string[]): boolean => {
    if (!user) return false;

    // Admin has all permissions
    if (user.role === 'PFADM') return true;

    const required = Array.isArray(permission) ? permission : [permission];
    return required.some((p) => permissions.includes(p));
  }, [user, permissions]);

  // Computed values
  const isBusinessUser = user ? BUSINESS_ROLES.includes(user.role as BusinessRole) : false;
  const isAdmin = user?.role === 'PFADM';

  const updateCliente = (updates: Partial<Cliente>): void => {
    if (cliente) {
      const updated = { ...cliente, ...updates };
      setCliente(updated);

      // Update session storage
      const session = authService.getCurrentSession();
      if (session) {
        authService.updateSession({ cliente: updated });
      }
    }
  };

  const clearProfileUpdateRequired = () => {
    setProfileUpdateRequired(false);
    const session = authService.getCurrentSession();
    if (session) {
      authService.updateSession({ requiresProfileUpdate: false } as any);
    }
  };

  const value: AuthContextType = {
    user,
    cliente,
    firebaseUser,
    loading,
    isAuthenticated: !!user && !!firebaseUser,
    permissions,
    entidadeId,
    isBusinessUser,
    isAdmin,
    purchasedApps,
    entityServiceTypes,
    entityServiceTypesLoaded,
    profileUpdateRequired,
    hasServiceType,
    login,
    logout,
    updateCliente,
    clearProfileUpdateRequired,
    hasPermission,
    hasPurchasedApp,
    refreshPurchasedApps,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
